import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ChannelType } from "helpers/enums";


interface IChannelsContext {
    recordCount: number
    search: ISearch
    channels: IChannel[]
    isLoading: boolean

    doSearch: (search: ISearch) => void
    handleChangePage: (event: any, page: number) => void
    updateSearch: (newSearch: ISearch) => void
    searchChannels: (searchRequest: ISearch) => Promise<void>
    deleteChannel: (id: string) => Promise<void>
}

export let channelsContext = React.createContext<IChannelsContext>({} as IChannelsContext);

let { Provider } = channelsContext;

interface IChannel {
    id: string
    channelCode: string
    channelType: ChannelType
    description: string
    countryCode: string
    allCountries: boolean
    automaticOrderRelease: boolean
    sendFeesEmail: boolean
    isDisabled: boolean
}

export interface ISearch {
    fullText: string
    showDisabled: boolean
    pageNr: number
    pageSize: number
}

export let ChannelsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let location = useLocation();
    let navigate = useNavigate();

    let [isLoading, setLoading] = useState(true);


    let [channels, setChannels] = useState<IChannel[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearch>({
        fullText: '',
        showDisabled: false,
        pageNr: 0,
        pageSize: 20
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Navigate to={location.pathname} state={{ search: search }} replace={true} />


    let processState = (state: any) => {
        if (state) {

            setSearch({
                ...search,
                ...state
            });

            searchChannels({
                ...search,
                ...state
            });
        }
    }

    let doSearch = (values: ISearch) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        navigate(location.pathname, { state: request });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchChannels = async (searchRequest: ISearch) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Channel/SearchChannels', searchRequest);

            setChannels(response.data.items);
            setTotalRecords(response.data.recordCount);
            setSearch({ ...searchRequest });

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        navigate(location.pathname, { state: request });
    }

    let deleteChannel = async (id: string) => {
        if (window.confirm("Are you sure you want to delete the channel?")) {
            try {
                await axios.delete(`/api/Channel/DeleteChannel/${id}`)
                setChannels(channels.filter(channel => channel.id !== id));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar("Channel deleted", { variant: 'success' });
            } catch (error) { }
        }
    }


    return (
        <Provider value={{
            recordCount,
            search,
            channels,
            isLoading,

            doSearch,
            handleChangePage,
            updateSearch,
            searchChannels,
            deleteChannel
        }}>
            {children}
        </Provider>
    );
};